import { BellRing, BookImage, Images, Lock, Smartphone, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { usePricingCopy } from "@/content/pricing";
import type { PricingCopy } from "@/content/pricing/copy.en";
import tile1 from "@/assets/for-participants-hero.jpg";
import tile2 from "@/assets/participant-finding-photos.jpg";
import tile3 from "@/assets/hero-sports.jpg";
import tile4 from "@/assets/photographer-action.jpg";
import tile5 from "@/assets/participant-search.jpg";
import tile6 from "@/assets/hero-face-detection.jpg";

const ICONS: Record<string, LucideIcon> = {
  private: Lock,
  albums: Users,
  event: Images,
  prints: BookImage,
};

const TILES = [tile1, tile2, tile3, tile4, tile5, tile6];

/** A decorative phone showing a "Just me" album. Hidden from assistive tech. */
const PhoneMock = ({ copy: COPY }: { copy: PricingCopy["app"] }) => (
  <div aria-hidden="true" className="relative mx-auto w-[240px] sm:w-[260px]">
    <div className="absolute -inset-10 rounded-full bg-hawk-orange/20 blur-3xl" />
    <div className="relative rounded-[2.5rem] border-[10px] border-hawk-navy-light bg-hawk-navy p-3 shadow-2xl ring-1 ring-white/10">
      <div className="mx-auto mb-3 h-5 w-24 rounded-full bg-black/60" />
      <div className="flex items-center justify-between px-1 pb-3">
        <div>
          <p className="text-sm font-display font-bold text-white">{COPY.phoneAlbum}</p>
          <p className="text-[11px] text-white/60">{COPY.phoneAlbumCount}</p>
        </div>
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-hawk-orange to-hawk-gold">
          <Users size={14} className="text-white" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1 overflow-hidden rounded-2xl">
        {[...TILES, ...TILES.slice(0, 3)].map((src, i) => (
          <img key={i} src={src} alt="" loading="lazy" className="aspect-square h-full w-full object-cover" />
        ))}
      </div>
      <div className="mx-auto mt-3 h-1 w-20 rounded-full bg-white/40" />
    </div>
  </div>
);

interface Props {
  className?: string;
}

/**
 * "Coming soon" teaser for the Photohawk app (Photohawk Personal). It's a separate subscription,
 * not part of the hosting plans, so keep it unlinked from them. Copy: `app` in
 * src/content/pricing/copy.<lang>.ts.
 * There's no waitlist/mailing endpoint for the marketing site yet, so the CTA is a disabled
 * placeholder: wire it to a real endpoint before enabling it.
 */
const AppComingSoon = ({ className }: Props) => {
  const COPY = usePricingCopy().copy.app;
  return (
  <section aria-labelledby="app-coming-soon-heading" className={cn("relative overflow-hidden bg-hawk-navy py-20 text-white", className)}>
    <div className="absolute inset-0 bg-radial-orange opacity-20" />
    <div className="container relative z-10 mx-auto px-4">
      <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-hawk-orange to-hawk-gold px-3 py-1 text-sm font-semibold text-white">
              <BellRing size={14} />
              {COPY.badge}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-sm">
              <Smartphone size={14} className="text-hawk-orange" />
              {COPY.platform}
            </span>
          </div>
          <h2 id="app-coming-soon-heading" className="mb-4 text-3xl font-display font-bold md:text-4xl">
            {COPY.heading}
          </h2>
          <p className="mb-8 max-w-xl text-lg text-white/80">{COPY.intro}</p>

          <ul className="mb-8 grid gap-5 sm:grid-cols-2">
            {COPY.points.map((p) => {
              const Icon = ICONS[p.id] ?? Images;
              return (
                <li key={p.id} className="flex gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/10">
                    <Icon size={18} className="text-hawk-orange" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{p.title}</h3>
                    <p className="text-sm text-white/70">{p.body}</p>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="flex flex-wrap items-center gap-4">
            <Button variant="heroOutline" size="lg" disabled aria-describedby="app-coming-soon-note">
              <BellRing />
              {COPY.cta}
            </Button>
            <p id="app-coming-soon-note" className="text-sm text-white/70">
              {COPY.ctaNote}
            </p>
          </div>
        </div>

        <PhoneMock copy={COPY} />
      </div>
    </div>
  </section>
  );
};

export default AppComingSoon;
